"use client";

import { Menu as MenuPrimitive } from "@base-ui/react/menu";
import type React from "react";
import { cn } from "@/lib/utils";

/**
 * DropdownMenu — a thin coss wrapper over Base UI's Menu, styled with the
 * popover token surface used across the app. Backs the shell's AccountChip and
 * WorkspaceSwitcher menus.
 */

export const DropdownMenu: typeof MenuPrimitive.Root = MenuPrimitive.Root;
export const DropdownMenuPortal: typeof MenuPrimitive.Portal =
  MenuPrimitive.Portal;
export const DropdownMenuGroup: typeof MenuPrimitive.Group =
  MenuPrimitive.Group;

export function DropdownMenuTrigger(
  props: MenuPrimitive.Trigger.Props,
): React.ReactElement {
  return <MenuPrimitive.Trigger data-slot="dropdown-menu-trigger" {...props} />;
}

export function DropdownMenuContent({
  className,
  children,
  side = "bottom",
  sideOffset = 6,
  align = "start",
  alignOffset = 0,
  portalProps,
  ...props
}: MenuPrimitive.Popup.Props & {
  side?: MenuPrimitive.Positioner.Props["side"];
  sideOffset?: MenuPrimitive.Positioner.Props["sideOffset"];
  align?: MenuPrimitive.Positioner.Props["align"];
  alignOffset?: MenuPrimitive.Positioner.Props["alignOffset"];
  portalProps?: MenuPrimitive.Portal.Props;
}): React.ReactElement {
  return (
    <MenuPrimitive.Portal {...portalProps}>
      <MenuPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        className="z-50 select-none outline-none"
        data-slot="dropdown-menu-positioner"
        side={side}
        sideOffset={sideOffset}
      >
        <MenuPrimitive.Popup
          className={cn(
            "min-w-44 origin-(--transform-origin) rounded-lg border bg-popover not-dark:bg-clip-padding p-1.5 text-popover-foreground shadow-lg/5 outline-none transition-[transform,opacity] duration-150 before:pointer-events-none before:absolute before:inset-0 before:rounded-[calc(var(--radius-lg)-1px)] before:shadow-[0_1px_--theme(--color-black/4%)] data-ending-style:opacity-0 data-starting-style:opacity-0 data-ending-style:scale-98 data-starting-style:scale-98 dark:before:shadow-[0_-1px_--theme(--color-white/6%)]",
            className,
          )}
          data-slot="dropdown-menu-content"
          {...props}
        >
          {children}
        </MenuPrimitive.Popup>
      </MenuPrimitive.Positioner>
    </MenuPrimitive.Portal>
  );
}

export function DropdownMenuItem({
  className,
  variant = "default",
  ...props
}: MenuPrimitive.Item.Props & {
  variant?: "default" | "danger";
}): React.ReactElement {
  return (
    <MenuPrimitive.Item
      className={cn(
        "flex min-h-8 cursor-default items-center gap-2.5 rounded-sm px-2.5 py-1.5 text-sm outline-none data-disabled:pointer-events-none data-highlighted:bg-accent data-highlighted:text-accent-foreground data-disabled:opacity-64 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg]:opacity-80",
        variant === "danger" &&
          "text-destructive-foreground data-highlighted:bg-destructive/8 data-highlighted:text-destructive-foreground",
        className,
      )}
      data-slot="dropdown-menu-item"
      {...props}
    />
  );
}

export function DropdownMenuSeparator({
  className,
  ...props
}: React.ComponentProps<"div">): React.ReactElement {
  return (
    <div
      className={cn("mx-1 my-1 h-px bg-border", className)}
      data-slot="dropdown-menu-separator"
      role="separator"
      {...props}
    />
  );
}

export function DropdownMenuLabel({
  className,
  ...props
}: React.ComponentProps<"div">): React.ReactElement {
  // A plain heading, not Menu.GroupLabel — that primitive requires a
  // Menu.Group ancestor, and this label is used standalone as a section title.
  return (
    <div
      className={cn(
        "px-2.5 py-1.5 font-medium text-muted-foreground text-xs",
        className,
      )}
      data-slot="dropdown-menu-label"
      {...props}
    />
  );
}

export { MenuPrimitive };
